import { useEffect, useRef } from "react";
import mixpanel from "mixpanel-browser";
import * as Sentry from "@sentry/react";

type QueuedEvent = {
  eventName: string;
  eventProperties: Record<string, any>;
};

const CLIENT_EVENT_PROPERTIES = {
  User: "Client",
};

class MixpanelTracker {
  /**
   * MixpanelTracker tracks events using Mixpanel.
   *
   * It handles the identification of users and queues events until the user is identified. This
   * allows for events to be tracked even if the user has not yet been identified and avoids
   * losing or misattributing events if the user is not identified before the event occurs.
   */

  constructor(
    private debug: boolean = process.env.NODE_ENV === "development",
  ) {}

  private hasIdentified = false;
  private queue: QueuedEvent[] = [];

  identify(userId: string) {
    mixpanel.identify(userId);
    mixpanel.people.set_once({ Role: "Client" });
    this.hasIdentified = true;

    this.flush();
  }

  reset() {
    // Capture any queued events before resetting in case we have a bug
    if (this.queue.length > 0) {
      Sentry.captureMessage(
        `MixpanelTracker: Resetting identity with ${this.queue.length} queued events.`,
        {
          level: "error",
        },
      );
    }

    if (this.debug) {
      console.info(
        `Resetting current mixpanel identity: ${mixpanel.get_distinct_id()}`,
      );
    }

    mixpanel.reset();
    this.hasIdentified = false;
    this.queue = [];
  }

  track(eventName: string, eventProperties: Record<string, any>) {
    if (!this.hasIdentified) {
      this.queue.push({
        eventName,
        eventProperties,
      });
    } else {
      if (this.debug) {
        console.info(
          `Would have fired mixpanel event for user ${mixpanel.get_distinct_id()}: ${eventName}`,
          eventProperties,
        );
      } else {
        try {
          mixpanel.track(eventName, eventProperties);
        } catch (e) {
          console.error("Error firing mixpanel event: ", e);
        }
      }
    }
  }

  private flush() {
    for (const { eventName, eventProperties } of this.queue) {
      mixpanel.track(eventName, eventProperties);
    }
    this.queue = [];
  }
}

const mixpanelTracker = new MixpanelTracker();

const useFireClientPortalMixpanelEventOnMount = (
  eventName: string,
  eventProperties: Record<string, any>,
  shouldFire: boolean = true,
) => {
  const hasFired = useRef(false);
  const isClientApp = process.env.REACT_APP_FAY_APP === "client";

  useEffect(() => {
    if (isClientApp && shouldFire && !hasFired.current) {
      hasFired.current = true;

      mixpanelTracker.track(eventName, {
        ...eventProperties,
        ...CLIENT_EVENT_PROPERTIES,
      });
    }
  }, [isClientApp, eventName, eventProperties, shouldFire]);
};

const fireClientPortalMixpanelEvent = (
  eventName: string,
  eventProperties: Record<string, any>,
) => {
  const isClientApp = process.env.REACT_APP_FAY_APP === "client";

  if (!isClientApp) {
    return;
  }

  mixpanelTracker.track(eventName, {
    ...eventProperties,
    ...CLIENT_EVENT_PROPERTIES,
  });
};

const mixpanelIdentifyClient = (userId: string) => {
  mixpanelTracker.identify(userId);
};

const mixpanelResetIdentity = () => {
  mixpanelTracker.reset();
};

export {
  useFireClientPortalMixpanelEventOnMount,
  fireClientPortalMixpanelEvent,
  mixpanelIdentifyClient,
  mixpanelResetIdentity,
};
